import styled from "@emotion/styled";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";

import { useDrawer, useFormAnalytics, useUser } from "@hmn/coolinarika-web-core/hooks";

import { Button, ButtonsRow, buttonVariants } from "../../../Button";
import { DefaultFallback, withErrorBoundary } from "../../../ErrorBoundary";
import { DynamicForm, getFormInitialValues, getFormValidation } from "../../../Form";
import { CheckMarkIcon, Icon } from "../../../Icon";
import { drawerUID } from "../../uids";
import styles from "./Forms.style";

const { RESET_PASSWORD: RESET_PASSWORD_DRAWER_UID } = drawerUID;

// Form inputs
const inputs = [
    {
        columns: [
            {
                layout: "form.column.auth",
                type: "text",
                id: "username",
                name: "username",
                autoComplete: "username",
                placeholder: "Korisničko ime ili e-mail adresa",
                validationType: "string",
                validations: [
                    {
                        type: "required",
                    },
                ],
            },
            {
                layout: "form.column.auth",
                type: "password",
                id: "password",
                name: "password",
                autoComplete: "current-password",
                placeholder: "Lozinka",
                validationType: "string",
                validations: [
                    {
                        type: "required",
                    },
                ],
            },
        ],
    },
];

const AuthFormLoginStyled = styled.div((props) => ({ ...styles(props) }));

const AuthFormLogin = ({ className, onLoginSuccess, ...rest }) => {
    const { logInWithCredentials } = useUser();

    const { open: openResetPasswordDrawer } = useDrawer(RESET_PASSWORD_DRAWER_UID);

    const { eventWithTrackingData } = useFormAnalytics();

    // Handle submit
    const onSubmit = async (values, { setFieldError }) => {
        const { username, password } = values;
        const loginData = await logInWithCredentials({ username, password });

        if (loginData && (loginData?.error || loginData?.message)) {
            setFieldError("username", "Pogrešno korisničko ime ili lozinka.");
            setFieldError("password", "Pogrešno korisničko ime ili lozinka.");
        } else if (!loginData?.error && !loginData?.message) {
            onLoginSuccess();
            eventWithTrackingData(
                {
                    elementLabel: "Login",
                },
                "coolinarika.userLogin",
            );
        }
    };

    return (
        <AuthFormLoginStyled className={className} {...rest}>
            <Formik
                initialValues={getFormInitialValues(inputs)}
                validationSchema={getFormValidation(inputs)}
                onSubmit={onSubmit}
            >
                {({ isSubmitting }) => (
                    <Form noValidate>
                        <DynamicForm fields={inputs} />
                        <ButtonsRow>
                            <Button
                                title="Prijavi me"
                                type="submit"
                                variant={buttonVariants.QUATERNARY}
                                iconRightComponent={<Icon icon={CheckMarkIcon} />}
                                disabled={isSubmitting}
                            >
                                Prijavi me
                            </Button>
                            <Button
                                title="Zaboravljena lozinka?"
                                onClick={openResetPasswordDrawer}
                                variant={buttonVariants.SECONDARY}
                                simple
                            >
                                Zaboravljena lozinka?
                            </Button>
                        </ButtonsRow>
                    </Form>
                )}
            </Formik>
        </AuthFormLoginStyled>
    );
};

AuthFormLogin.propTypes = {
    className: PropTypes.string,
    onLoginSuccess: PropTypes.func,
};

AuthFormLogin.defaultProps = {
    className: undefined,
    onLoginSuccess: undefined,
};

export default withErrorBoundary(AuthFormLogin, {
    FallbackComponent: (err) => <DefaultFallback err={err} />,
});
